import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { Field as FormikField, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import FolderField from '../../../components/TextField/Folder/FolderField'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import Fieldset from '../Fieldset'
import FormContainer from '../../../components/Form/FormContainer'
import { PivotProps } from './FileForm'
import { Field, Input } from '@fluentui/react-components'
import FileStatusPicker from '../../TagPicker/FileStatusPicker'
import { useTranslation } from 'react-i18next'
import LocalDatePicker from '../../DatePicker/LocalDatePicker'
import UserPicker from '../../TagPicker/UserPicker'
import UserCombobox from '../../Combobox/UserCombobox'
import LanguageCombobox from '../../Combobox/i18n/LanguageCombobox'
import { useSettings } from '../../../hooks/useSettings'

export default function GeneralPivotItem(props: PivotProps): ReactElement {
    const settings = useSettings()
    const { t: translate } = useTranslation()

    const { errors, values, setFieldValue, status, setStatus, initialValues } = useFormikContext<FileMutationDto>()
    const [suggestedPath, setSuggestedPath] = useState<string>()

    useEffect(() => {
        if (initialValues.reference && !initialValues.documentRootId && settings?.fileSharepointDisplayName) {
            setSuggestedPath(
                settings?.fileSharepointDisplayName
                    .replaceAll(/{{\s*file\.name\s*}}/g, values.name)
                    .replaceAll(/{{\s*file\.reference\s*}}/g, values.reference)
                    .replaceAll(/[/\\]/g, '-')
                    .trim(),
            )
        }
    }, [values.reference, values.name, settings])

    return (
        <FormContainer>
            <Field label={translate('file.edit.form.general.ownerId')} required orientation={props.orientation}>
                <UserCombobox
                    required
                    selectedOptions={[values.ownerId]}
                    onOptionSelect={(_, data) => setFieldValue('ownerId', data.optionValue)}
                />
            </Field>
            <Field label={translate('file.edit.form.general.executiveIds')} orientation={props.orientation}>
                <UserPicker
                    selectedOptions={values.executiveIds || []}
                    onOptionsSelect={(_, data) => setFieldValue('executiveIds', data)}
                />
            </Field>
            <FormikField name="documentRootId" validate={() => status.documentRootErrorMessage}>
                {() => (
                    <FolderField
                        initialItemId={initialValues.documentRootId}
                        suggestedPath={suggestedPath}
                        onChangePath={(path: string, item?: DriveItem, errorMessage?: string) => {
                            setStatus({
                                ...(status || {}),
                                documentRootPath: !errorMessage ? path : undefined,
                                documentRootErrorMessage: errorMessage,
                            })

                            setFieldValue('documentRootId', item?.id)
                        }}
                        textFieldProps={{ required: true, errorMessage: errors?.documentRootId }}
                    />
                )}
            </FormikField>
            <Fieldset legend={translate('file.edit.form.general.other')} canToggle>
                <Field label={translate('file.edit.form.general.language')} required>
                    <LanguageCombobox
                        required
                        selectedOptions={[values.language]}
                        onOptionSelect={(_, data) => setFieldValue('language', data.selectedOptions?.[0])}
                    />
                </Field>
                <Field label={translate('file.edit.form.general.archivelocation')}>
                    <Input
                        value={values.archiveLocation}
                        onChange={(_, data) => setFieldValue('archiveLocation', data.value)}
                    />
                </Field>
                <Field label={translate('file.edit.form.general.dateincident')}>
                    <LocalDatePicker
                        isoValue={values.dateOfIncident}
                        onSelectDate={date => setFieldValue('dateOfIncident', date)}
                        maxDate={new Date()}
                    />
                </Field>
                <Field label={translate('file.edit.form.general.status')} required>
                    <FileStatusPicker
                        singleSelect
                        selectedOptions={[values.status]}
                        onOptionsSelect={(_, selectedOptions) => setFieldValue('status', selectedOptions?.[0])}
                    />
                </Field>
            </Fieldset>
        </FormContainer>
    )
}
